// frontend/src/components/placements/PlacementList.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const PlacementList = ({ onEdit, onCreate, onView }) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [cities, setCities] = useState([]);

  const loadPlacements = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const params = {
        tenant_id: user.tenant_id,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (cityFilter) {
        params.city = cityFilter;
      }

      const res = await secureAxios.get(`${apiBase}/api/placements`, {
        params,
      });
      setPlacements(res.data);

      // Extract unique cities for filter dropdown
      const uniqueCities = [
        ...new Set(res.data.map((p) => p.employer_city).filter(Boolean)),
      ].sort();
      setCities(uniqueCities);
    } catch (err) {
      console.error("Error loading placements:", err);
      toast.error("Failed to load placements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlacements();
  }, [user?.tenant_id, statusFilter, cityFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadPlacements();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDelete = async (placement) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this placement for ${placement.consumer_name}?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/placements/${placement.placement_id}`,
        {
          params: { tenant_id: user.tenant_id },
        }
      );
      toast.success("Placement deleted successfully");
      loadPlacements();
    } catch (err) {
      console.error("Error deleting placement:", err);
      toast.error(err.response?.data?.error || "Failed to delete placement");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: "bg-warning text-dark", label: "Pending" },
      active: { class: "bg-success", label: "Active" },
      "closed-successful": { class: "bg-info", label: "Closed - Successful" },
      "closed-unsuccessful": {
        class: "bg-danger",
        label: "Closed - Unsuccessful",
      },
    };
    const badge = badges[status] || badges.pending;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="container my-4">
      {/* Search and Filters */}
      <div className="row g-2 mb-2 mb-md-3">
        <div className="col-md-3">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
          <small className="text-muted">
            Search by consumer, employer, job title, or city
          </small>
        </div>
        <div className="col-md-2">
          <select
            className="form-select form-select-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="closed-successful">Closed - Successful</option>
            <option value="closed-unsuccessful">Closed - Unsuccessful</option>
          </select>
        </div>
        <div className="col-md-2">
          <select
            className="form-select form-select-sm"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="">All Cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          {(searchTerm || statusFilter || cityFilter) && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
                setCityFilter("");
              }}
            >
              <i className="bi bi-x-circle me-1"></i>
              Clear Filters
            </button>
          )}
        </div>
        <div className="col-12 col-md-3 text-end">
          <button className="btn btn-primary" onClick={onCreate}>
            <i className="bi bi-plus-circle me-2"></i>
            New Placement
          </button>
        </div>
      </div>

      {/* Placements Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : placements.length === 0 ? (
        <div className="text-center py-5">
          <i
            className="bi bi-briefcase text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Placements Found</h5>
          <p className="text-muted">
            {searchTerm || statusFilter
              ? "Try adjusting your search or filter criteria"
              : "Record your first job placement to get started"}
          </p>
          {!searchTerm && !statusFilter && (
            <button className="btn btn-primary" onClick={onCreate}>
              <i className="bi bi-plus-circle me-2"></i>
              Add First Placement
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive d-none d-lg-block">
            <table className="table table-hover table-sm">
              <thead className="table-light">
                <tr>
                  <th>Consumer</th>
                  <th>Employer</th>
                  <th>Job Title</th>
                  <th>Wage</th>
                  <th>Hrs/Wk</th>
                  <th>Start</th>
                  <th>Status</th>
                  <th>Placed By</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {placements.map((placement) => (
                  <tr key={placement.placement_id}>
                    <td>
                      <strong>{placement.consumer_name || "Unassigned"}</strong>
                    </td>
                    <td>
                      {placement.company_name || "-"}
                      {placement.employer_city && (
                        <small className="text-muted d-block">
                          {placement.employer_city}
                        </small>
                      )}
                    </td>
                    <td>{placement.job_title || "-"}</td>
                    <td>{formatCurrency(placement.hourly_wage)}/hr</td>
                    <td>{placement.hours_per_week || "-"}</td>
                    <td>{formatDate(placement.start_date)}</td>
                    <td>{getStatusBadge(placement.status)}</td>
                    <td>{placement.placed_by_name || "-"}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-info"
                          onClick={() => onView(placement)}
                          title="View"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => onEdit(placement)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(placement)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-lg-none">
            {placements.map((placement) => (
              <div key={placement.placement_id} className="card mb-2">
                <div className="card-body p-2">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-bold text-truncate">
                        {placement.consumer_name || "Unassigned"}
                      </div>
                      <div className="small text-muted text-truncate">
                        {placement.job_title || "No job title"}
                      </div>
                    </div>
                    <div className="ms-2">
                      {getStatusBadge(placement.status)}
                    </div>
                  </div>
                  <div className="small mb-2">
                    <div className="d-flex flex-wrap gap-2 text-muted">
                      <span>
                        <i className="bi bi-building me-1"></i>
                        {placement.company_name || "-"}
                      </span>
                      <span>
                        <i className="bi bi-currency-dollar me-1"></i>
                        {formatCurrency(placement.hourly_wage)}/hr
                      </span>
                      <span>
                        <i className="bi bi-clock me-1"></i>
                        {placement.hours_per_week || "-"} hrs
                      </span>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {formatDate(placement.start_date)}
                    </small>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-info btn-sm"
                        onClick={() => onView(placement)}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onEdit(placement)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(placement)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Summary Stats */}
      {!loading && placements.length > 0 && (
        <div className="row g-2 mt-2 mt-md-3">
          <div className="col">
            <div className="d-flex gap-3 text-muted small">
              <span>
                Total: <strong>{placements.length}</strong>
              </span>
              <span>
                Active:{" "}
                <strong>
                  {placements.filter((p) => p.status === "active").length}
                </strong>
              </span>
              <span>
                Pending:{" "}
                <strong>
                  {placements.filter((p) => p.status === "pending").length}
                </strong>
              </span>
              <span>
                Successful:{" "}
                <strong>
                  {
                    placements.filter((p) => p.status === "closed-successful")
                      .length
                  }
                </strong>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementList;